/**
 * Cloud Agent — frontend service client (GENUI-3, Phase 3).
 *
 * Thin wrapper over the super-admin Cloud Agent endpoints. Mirrors the
 * `adminService` fetch pattern (bearer token + JSON + error logging).
 */
import { getAccessToken } from '../authentication/supabaseService';
import { readSseStream, type SseFrame } from '../../utils/sseUtils';
import type {
  CloudAgentScope,
  ConversationUsage,
  HydratedBlock,
  HydratedSpec,
  LayoutBlock,
  LayoutSpec,
  Side,
  TurnAnswer,
  UsageStat,
} from './cloudAgentTypes';

const API_BASE = '/api/admin';

/**
 * A failed Cloud Agent request, carrying the server's own `error` string as `code`.
 *
 * The routes answer with machine-readable codes (`scope_mismatch`, `conversation_not_found`)
 * before the SSE headers are flushed, so the caller can recover instead of just reporting a
 * status line. `code` is null when the body carried no `error` field.
 */
export class CloudAgentRequestError extends Error {
  constructor(
    message: string,
    readonly code: string | null,
    readonly status: number
  ) {
    super(message);
    this.name = 'CloudAgentRequestError';
  }
}

/** Read a failed response's `{ error }` body, falling back to the status line. */
async function toRequestError(response: Response, endpoint: string): Promise<CloudAgentRequestError> {
  let code: string | null = null;
  try {
    const body = await response.json();
    if (body && typeof body.error === 'string') code = body.error;
  } catch {
    // Non-JSON body (proxy error page, empty 500) — the status line is all we have.
  }
  return new CloudAgentRequestError(
    code ?? `Failed to fetch ${endpoint}: ${response.statusText}`,
    code,
    response.status
  );
}

async function fetchWithAuth<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const token = await getAccessToken();
  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });

  if (!response.ok) {
    throw await toRequestError(response, endpoint);
  }

  return response.json();
}

/** One restaurant inside a scope client. */
export interface ScopeRestaurant {
  restaurantId: string;
  restaurantName: string;
}

/** An admin client with their restaurants (scope-selector source). */
export interface ScopeClient {
  clientId: string;
  clientName: string | null;
  clientEmail: string;
  restaurants: ScopeRestaurant[];
}

export interface ScopesResponse {
  clients: ScopeClient[];
  /**
   * How many restaurants the synthetic "All restaurants" scope covers. Optional — when the
   * server omits it the caller counts the restaurants it already has.
   */
  allRestaurantsCount?: number;
}

/** Body of `POST /cloud-agent/ask/stream` — the conversational v2 contract. */
export interface AskStreamRequest {
  /** Omit on the first turn; the server creates one and returns it in the `meta` frame. */
  conversationId?: string;
  scope: CloudAgentScope;
  question: string;
}

/** Fetch the clients-with-restaurants source that backs the scope selector. */
export async function listScopes(): Promise<ScopesResponse> {
  try {
    return await fetchWithAuth<ScopesResponse>('/cloud-agent/scopes');
  } catch (err) {
    console.error('[CloudAgent] Failed to load scopes:', err);
    throw err;
  }
}

/** The `meta` frame — always first, and how the frontend learns its `conversationId`. */
export interface AskStreamMeta {
  conversationId: string;
  turnIndex: number;
  scope: CloudAgentScope;
  model: string;
}

/** Live callbacks for the streaming (SSE) ask — each fires as the backend emits it. */
export interface AskStreamHandlers {
  /** Conversation identity for this turn. Always the first frame. */
  onMeta?: (meta: AskStreamMeta) => void;
  /** A human-readable progress line ("Reading <restaurant> data…"), only while tools run. */
  onStatus?: (message: string) => void;
  /** One chunk of streamed assistant prose. */
  onText?: (delta: string) => void;
  /** One hydrated block, in order, as it is produced. */
  onBlock?: (block: HydratedBlock) => void;
  /** The finished turn: prose + optional hydrated spec + the depth the agent chose. */
  onDone?: (answer: TurnAnswer) => void;
  /** Per-turn and running per-conversation token/cost accounting (after `done`). */
  onUsage?: (turn: UsageStat, conversation: ConversationUsage) => void;
  /** Total Claude cost once the run finishes (kept for compat with the v1 frame). */
  onComplete?: (costUsd: number) => void;
}

/**
 * Dispatch one parsed SSE frame onto the handlers. An `error` event throws so the caller's
 * catch surfaces it (no silent failures).
 */
function dispatchSseFrame({ event, payload }: SseFrame, handlers: AskStreamHandlers): void {
  switch (event) {
    case 'meta':
      handlers.onMeta?.(payload as unknown as AskStreamMeta);
      break;
    case 'status':
      handlers.onStatus?.(payload.message as string);
      break;
    case 'text':
      handlers.onText?.((payload.delta as string) ?? '');
      break;
    case 'block':
      handlers.onBlock?.(payload.block as HydratedBlock);
      break;
    case 'done':
      handlers.onDone?.(payload.answer as unknown as TurnAnswer);
      break;
    case 'usage':
      handlers.onUsage?.(
        payload.turn as unknown as UsageStat,
        payload.conversation as unknown as ConversationUsage
      );
      break;
    case 'complete':
      handlers.onComplete?.((payload.costUsd as number) ?? 0);
      break;
    case 'error':
      throw new Error((payload.error as string) || 'Cloud Agent stream error');
    default:
      break;
  }
}

/**
 * Ask the Cloud Agent with live streaming (SSE over a POST fetch — keeps the Bearer
 * token in the header, unlike `EventSource`). Resolves when the stream ends; rejects
 * on a transport error or a server-sent `error` event. Pass `signal` to cancel an
 * in-flight stream (e.g. when a newer question supersedes it or the page unmounts).
 */
export async function askStream(
  params: AskStreamRequest,
  handlers: AskStreamHandlers,
  signal?: AbortSignal
): Promise<void> {
  const token = await getAccessToken();
  const response = await fetch(`${API_BASE}/cloud-agent/ask/stream`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(params),
    signal,
  });

  // Validation / conversation-lookup failures answer as JSON *before* the SSE headers, so a
  // non-OK response still carries a usable code (`scope_mismatch`, `conversation_not_found`).
  if (!response.ok) {
    throw await toRequestError(response, '/cloud-agent/ask/stream');
  }
  if (!response.body) {
    throw new CloudAgentRequestError('Cloud Agent stream returned no body', null, response.status);
  }

  await readSseStream(response.body, (frame) => dispatchSseFrame(frame, handlers));
}

// ── Re-hydration (filter + pinned views) ────────────────────────────────────

export interface RehydrateParams {
  scope: CloudAgentScope;
  layout: LayoutSpec;
}

/**
 * Re-run the server hydrator over a data-free layout — NO Claude call. Backs the interactive
 * `filter` block (re-scope by side) and re-opening a pinned view (fresh numbers).
 */
export async function rehydrate(params: RehydrateParams): Promise<{ spec: HydratedSpec }> {
  try {
    return await fetchWithAuth<{ spec: HydratedSpec }>('/cloud-agent/rehydrate', {
      method: 'POST',
      body: JSON.stringify(params),
    });
  } catch (err) {
    console.error('[CloudAgent] Rehydrate failed:', err);
    throw err;
  }
}

/**
 * Free the server-side conversation for `conversationId`. Best-effort: the store is in-memory
 * with a TTL, so a failure here only means the entry lingers until it is swept.
 */
export async function deleteConversation(conversationId: string): Promise<void> {
  try {
    await fetchWithAuth<{ ok?: boolean }>(`/cloud-agent/conversation/${conversationId}`, {
      method: 'DELETE',
    });
  } catch (err) {
    console.error('[CloudAgent] Failed to delete conversation:', err);
  }
}

/**
 * Strip a hydrated spec back to its data-free layout (computation refs + params only). This is
 * what we send to `/rehydrate` when a filter block changes — never the hydrated numbers.
 */
export function hydratedSpecToLayout(spec: HydratedSpec): LayoutSpec {
  const blocks: LayoutBlock[] = spec.blocks.map((b): LayoutBlock => {
    if (b.type === 'callout') {
      return b.tone ? { type: 'callout', text: b.text, tone: b.tone } : { type: 'callout', text: b.text };
    }
    if (b.type === 'sectionHeading') {
      return { type: 'sectionHeading', text: b.text };
    }
    if (b.type === 'filter') {
      return { type: 'filter', text: b.text, param: b.param, options: b.options, selected: b.selected };
    }
    // Computation-backed block: keep only the layout fields (drop data + source).
    const out: Record<string, unknown> = {
      type: b.type,
      computation: b.computation,
      params: { ...b.params },
    };
    if (b.title) out.title = b.title;
    if (b.icon) out.icon = b.icon;
    if (b.tint) out.tint = b.tint;
    return out as unknown as LayoutBlock;
  });
  return { title: spec.title, summary: spec.summary, keyPoints: spec.keyPoints, blocks };
}

/** Map a filter's checkbox selection to the effective `side`: one of food/drinks ⇒ that side, else all. */
export function effectiveSide(selected: string[], options: string[]): Side {
  const valid = selected.filter((s) => options.includes(s));
  if (valid.length === 1 && (valid[0] === 'food' || valid[0] === 'drinks')) return valid[0];
  return 'all';
}

/** Apply a `side` override to every computation block of a layout (pass-through blocks untouched). */
export function applySideToLayout(layout: LayoutSpec, side: Side): LayoutSpec {
  const passThrough = new Set(['callout', 'sectionHeading', 'filter']);
  return {
    ...layout,
    blocks: layout.blocks.map((b) => {
      if (passThrough.has(b.type)) return b;
      const cb = b as Extract<LayoutBlock, { computation: string }>;
      return { ...cb, params: { ...cb.params, side } };
    }),
  };
}

